import {
  useDebouncedValue,
  useElementSize,
  useIntersection,
  useViewportSize,
} from '@mantine/hooks';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  Fade,
  FormControl,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Popper,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { useInfiniteQuery } from '@tanstack/react-query';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import { TFunction } from 'i18next';
import { Fragment, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import {
  getMockOutlinedInputSx,
  getMockOutlinedLabelSx,
  resolveSxProps,
} from 'utility/styles';
import { DynamicFieldChip } from '../components/DynamicFieldChip';
import { DenumFieldProps } from './DenumField';
import { DynamicFieldContextType } from '../DynamicFieldContext';
import {
  DROPDOWN_MAX_HEIGHT,
  getFieldLabel,
  normalizeSelectedValues,
} from '../utility/functions';
import { getCheckRadioSx, getIconButtonSx } from '../utility/styles';

/**
 * useViewportDebounced
 * https://mantine.dev/hooks/use-viewport-size/
 *
 * Returns debounced viewport width and height values.
 * Note: first render may return 0s before measured.
 *
 * Can be mounted to cause component rerender on viewport
 * size changes to nudge certain poppers (eg. dropdowns)
 * to reposition themselves, which can be neccesary if
 * the browser was minimized/maximized and an event
 * was not detected by the poppers resize observer.
 *
 */

type UseViewportDbArgs = {
  debounceMs?: number;
};

type UseViewportDbReturn = {
  dbWidth: number;
  dbHeight: number;
};

export function useViewportDebounced({ debounceMs = 60 }: UseViewportDbArgs) {
  const { width, height } = useViewportSize();
  const [dbWidth] = useDebouncedValue(width, debounceMs);
  const [dbHeight] = useDebouncedValue(height, debounceMs);

  const result: UseViewportDbReturn = useMemo(() => {
    return {
      dbWidth: dbWidth,
      dbHeight: dbHeight,
    };
  }, [dbWidth, dbHeight]);

  return result;
}

/**
 * getCountItemsText
 * Sometimes api bug returns null / 0 for total.
 *
 * Will conditionally change footer text based
 * on if api total is valid, invalid, or no
 * local state options setup yet.
 *
 * loading: "Loading..."
 * valid api total: "(x) results - (y) total"
 * invalid api total: "(x) results"
 * No options: "No results found"
 *
 */

type GetCountItemsTextArgs = {
  t: TFunction;
  isLoading: boolean;
  apiTotal: number | null;
  fetchedLength: number;
};

export function getCountItemsText({
  t,
  isLoading,
  apiTotal,
  fetchedLength,
}: GetCountItemsTextArgs): string {
  if (isLoading) {
    return t('blox.general.loading');
  }
  const itemCount = fetchedLength ?? 0;
  if (itemCount === 0) {
    return t('blox.general.no_results_found');
  }
  const strResults = t('blox.general.count_results', { count: itemCount });
  const nApiTotal = apiTotal ?? 0;
  if (!nApiTotal) {
    return strResults;
  }
  const strTotal = t('blox.general.count_total', { count: nApiTotal });
  return `${strResults} - ${strTotal}`;
}

/**
 * -------------------------------------------------
 * Alternative denum field design
 * -------------------------------------------------
 * Dynamic Fields Docs
 * https://townnews.atlassian.net/wiki/spaces/COSMOS/pages/2881847298/
 *
 * Alternative display for denum fields, more modal-friendly.
 * Chips selected values, search input for the API query and
 * a popover listbox of dynamic options with infinite scroll.
 *
 * Manual control of popover/keyborard interactions for better
 * a11y and to work around limitations of MUIs Select/Autocomplete
 * components when it comes to custom input + popover design.
 *
 * Design is similar to the "Multitext" dynamic field but adapted
 * using the text input as a typeahead search instead of add entries.
 *
 */

// any generic, see DenumField.tsx for more accurate type
// if using for more than simple invocation next page fetch.
type FetchNextDenumPage = ReturnType<typeof useInfiniteQuery<any>>['fetchNextPage'];

type DenumFieldDropdownProps = {
  fieldConfig: DynamicFieldContextType['fieldConfig'];
  selectedValues: DynamicFieldContextType['selectedValues'];
  dynamicOptions: DynamicFieldContextType['dynamicOptions'];
  fetchedOptions: DynamicFieldContextType['fetchedOptions'];
  searchText: DynamicFieldContextType['searchText'];
  updateSearchText: DynamicFieldContextType['updateSearchText'];
  disabled: DenumFieldProps['disabled'];
  fieldSx?: DenumFieldProps['fieldSx'];
  onToggle: (value: string) => void;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: FetchNextDenumPage;
  apiTotal: number | null;
};

type HandleListItemRefsArgs = {
  isFirstItem: boolean;
  isLastItem: boolean;
};

const LOADING_DB_MS = 120;

export const DenumFieldDropdown = memo(
  ({
    fieldConfig,
    selectedValues,
    dynamicOptions,
    fetchedOptions,
    searchText,
    updateSearchText,
    disabled,
    fieldSx,
    onToggle,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    apiTotal,
  }: DenumFieldDropdownProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    // Popper open state
    const [popperOpen, setPopperOpen] = useState(false);

    // Popper is generally good at positioning to the ref but
    // in some edge cases like browser minimize/maximized resize
    // and rendered in a modal that also moves to fit in viewport,
    // noticed it was not repositioning properly. Watch the viewport
    // with debounced hook triggers a render to nudge back into place.
    useViewportDebounced({ debounceMs: 60 });

    // Translation strings
    const strNoSelections = t('blox.general.no_selections');
    const strSearch = t('blox.general.search');
    const strClear = t('blox.actions.clear');
    const strOpen = t('blox.actions.open');
    const strClose = t('blox.actions.close');
    const strLoading = t('blox.general.loading');
    const searchPlaceholderTxt = `${strSearch}...`;
    const arrowBtnTxt = popperOpen ? strClose : strOpen;

    // Controls display of certain items (checkbox / radio, etc.)
    const isMultiSelect = useMemo(() => {
      return Boolean(fieldConfig?.config?.multiple);
    }, [fieldConfig]);

    // Clean string label for mui outlined
    const fieldLabel = useMemo(() => {
      return getFieldLabel(fieldConfig);
    }, [fieldConfig]);

    const fieldName = useMemo(() => {
      return fieldConfig?.name ?? '';
    }, [fieldConfig?.name]);

    // Intersection infinite scroll ref
    const { ref: ixRef, entry } = useIntersection({ threshold: 0.5 });

    // Ref mui input width to match popover width
    const { ref: muiFieldRef, width: muiFieldWidth } = useElementSize<HTMLDivElement>();

    // Refs to elements for focus management
    const textInputRef = useRef<HTMLInputElement>(null);
    const arrowBtnRef = useRef<HTMLButtonElement>(null);
    const listBoxRef = useRef<HTMLUListElement>(null);
    const firstItemRef = useRef<HTMLDivElement | null>(null);
    const lastItemRef = useRef<HTMLDivElement | null>(null);

    // Normalize to string[] for render/handlers
    // using same helper as SelectedItemsAccordion
    // chips value normalization for consistency
    const nValues: string[] = useMemo(() => {
      return normalizeSelectedValues(selectedValues);
    }, [selectedValues]);

    const hasSelections = useMemo(() => {
      return nValues.length > 0;
    }, [nValues.length]);

    const isAnyFetching = useMemo(() => {
      return isFetching || isFetchingNextPage;
    }, [isFetching, isFetchingNextPage]);

    // Handle removal selection via chip "x" button
    const handleRemoveChip = useCallback(
      (val: string) => {
        onToggle(val);
      },
      [onToggle],
    );

    // Handle select/deselect option from popover list
    const handleOptionToggle = useCallback(
      (val: string) => {
        onToggle(val);
      },
      [onToggle],
    );

    // Using popper for better a11y as Popover has a backdrop
    // which can block interactions with the text input.
    const handlePopperClose = useCallback(
      (event: MouseEvent | TouchEvent | React.KeyboardEvent) => {
        // guard: allow user trying to click the input
        if (muiFieldRef?.current?.contains(event?.target as Node)) {
          return;
        }
        setPopperOpen(false);
      },
      [muiFieldRef],
    );

    /**
     * Popper Keydown Handler
     * - Escape: close popper, focus arrow button
     * - Tab: manages focus within popper, close on tab out
     * Generally better a11y manually vs MUI Select/Autocomplete issues
     * Note modern js checks event.key or event.code properties
     * older event.which and event.keyCode are deprecated.
     * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
     */
    const handlePaperKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
          handlePopperClose(e);
          arrowBtnRef.current?.focus();
        }
        // No items: any Tab press should exit the popover
        if (e.key === 'Tab' && fetchedOptions.length === 0) {
          e.preventDefault();
          setPopperOpen(false);
          arrowBtnRef.current?.focus();
        }
        // Tab backwards from first item: redirect focus back to the input
        if (e.key === 'Tab' && e.shiftKey && e.target === firstItemRef.current) {
          e.preventDefault();
          textInputRef.current?.focus();
        }
        // Tab forwards from last item: exit popover to arrow button
        if (e.key === 'Tab' && !e.shiftKey && e.target === lastItemRef.current) {
          e.preventDefault();
          setPopperOpen(false);
          arrowBtnRef.current?.focus();
        }
      },
      [fetchedOptions.length, handlePopperClose],
    );

    /**
     * Text Input Handlers
     * - Focus input to open popover
     * - Update search text on change
     * - Handle keydown escape (close popover) and tab (focus first item)
     * - Icon Arrow - open/close popover, focus input on open
     */
    const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      setPopperOpen(true);
    }, []);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSearchText(e.target.value);
      },
      [updateSearchText],
    );

    const handleInputKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        // guard: keydown bubbles from child elements (eg: arrow btn)
        // only handle events originating from the input itself
        if (e.target !== textInputRef.current) {
          return;
        }
        // Escape: close popover, move focus to arrow button
        if (e.key === 'Escape') {
          setPopperOpen(false);
          arrowBtnRef.current?.focus();
        }
        // Tab forward: focus first list item in
        // popover if it exists (not empty list)
        if (e.key === 'Tab' && !e.shiftKey && popperOpen && firstItemRef.current) {
          e.preventDefault();
          firstItemRef.current?.focus();
        }
        // Tab backwards: close popover, exiting from input
        if (e.key === 'Tab' && e.shiftKey) {
          setPopperOpen(false);
        }
      },
      [popperOpen],
    );

    const handleClearInput = useCallback(() => {
      updateSearchText('');
      textInputRef.current?.focus();
    }, [updateSearchText]);

    const handleArrowClick = useCallback(() => {
      if (!popperOpen) {
        setPopperOpen(true);
        textInputRef.current?.focus();
      } else {
        setPopperOpen(false);
      }
    }, [popperOpen]);

    /**
     * footer - items count
     * debounced for transition during typeahead searching
     * to prevent rapid footer text changes during api call
     */
    const footerText = useMemo(() => {
      return getCountItemsText({
        t: t,
        isLoading: isAnyFetching,
        apiTotal: apiTotal,
        fetchedLength: fetchedOptions.length,
      });
    }, [t, isAnyFetching, apiTotal, fetchedOptions.length]);

    const [dbFooterText] = useDebouncedValue(footerText, LOADING_DB_MS);

    /**
     * Returns a callback ref that assigns firstItemRef, lastItemRef, or both.
     * Setup and assign first and last item refs for keyboard focus mgmt.
     * Primarily for simpler code flow of keyboard focus management
     * functions handleInputKeyDown, handlePaperKeyDown, etc.
     *
     * Handle if the list has only one item, both are set, so single element
     * acts as both the first and last - keeping Tab-in (firstItemRef) and
     * Tab-out (lastItemRef) keyboard handling correct for all list sizes
     */
    const handleListItemRefs = useCallback(
      ({
        isFirstItem,
        isLastItem,
      }: HandleListItemRefsArgs): React.RefCallback<HTMLDivElement> =>
        (node) => {
          if (isFirstItem) {
            firstItemRef.current = node;
          }
          if (isLastItem) {
            lastItemRef.current = node;
          }
        },
      [],
    );

    /**
     * Mock MUI Styles
     */
    const mockOutlinedSx = useMemo(() => {
      return getMockOutlinedInputSx(theme, false);
    }, [theme]);

    const mockLabelSx = useMemo(() => {
      return getMockOutlinedLabelSx(theme);
    }, [theme]);

    const resolvedSx: SxProps<Theme> = useMemo(() => {
      return resolveSxProps(fieldSx, theme);
    }, [fieldSx, theme]);

    const wrapperSx: SxProps<Theme> = useMemo(() => {
      return {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        mt: 2,
        mb: 0.5,
        pt: 1.5,
        pb: 1,
        px: 1,
        borderRadius: 1,
        gap: 0.25,
        ...mockOutlinedSx,
        ...resolvedSx,
      };
    }, [mockOutlinedSx, resolvedSx]);

    // Base styles for input end icons
    const baseIconSx: SxProps<Theme> = useMemo(() => {
      return getIconButtonSx(theme);
    }, [theme]);

    // Show clear input icon when text exists
    const clearIconSx: SxProps<Theme> = useMemo(() => {
      return {
        ...baseIconSx,
        display: searchText ? 'inline-flex' : 'none',
      };
    }, [baseIconSx, searchText]);

    // Rotate arrow if open popper
    const arrowIconSx: SxProps<Theme> = useMemo(() => {
      return {
        ...baseIconSx,
        transform: popperOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
          duration: theme.transitions.duration.shortest,
        }),
      };
    }, [baseIconSx, popperOpen, theme.transitions]);

    // Checkbox/Radio styles
    const checkRadioSx: SxProps<Theme> = useMemo(() => {
      return getCheckRadioSx(theme);
    }, [theme]);

    return (
      <FormControl
        data-testid={`dynamic-field-denum-${fieldName}`}
        sx={wrapperSx}
        disabled={disabled}
        margin="none"
        variant="outlined"
      >
        <Typography
          data-testid={`denum-label-${fieldName}`}
          sx={mockLabelSx}
        >
          {fieldLabel}
        </Typography>

        <Box
          data-testid={`denum-chips-container-${fieldName}`}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            pt: 1,
          }}
        >
          {!hasSelections ? (
            <Typography
              data-testid={`denum-chips-empty-${fieldName}`}
              sx={{
                fontSize: bloxValues.smallFontSizeRem,
                color: theme.palette.text.disabled,
              }}
            >
              {strNoSelections}
            </Typography>
          ) : (
            <Fragment>
              {nValues.map((val, idx) => {
                // Chips should read from dynamicOptions instead of fetchedOptions
                // as they may include items from parent not yet fetched on page.
                const opt = dynamicOptions?.find((o) => o?.value === val);
                const label = opt?.label || val;
                const keyId = `key-${val}-${idx}`;
                return (
                  <DynamicFieldChip
                    key={keyId}
                    idTag="denum"
                    value={val}
                    label={label}
                    onRemove={handleRemoveChip}
                    disabled={disabled}
                  />
                );
              })}
            </Fragment>
          )}
        </Box>

        <Box
          data-testid={`denum-search-input-${fieldName}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <TextField
            label={null}
            ref={muiFieldRef as React.Ref<HTMLDivElement>}
            size="small"
            variant="outlined"
            placeholder={searchPlaceholderTxt}
            disabled={disabled}
            value={searchText}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
            fullWidth={true}
            autoComplete="off"
            inputRef={textInputRef}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: bloxValues.smallFontSizeRem,
                display: 'flex',
                alignItems: 'center',
                p: 1,
              },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <Box
                    data-testid={`denum-search-actions-${fieldName}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Fade
                      appear={true}
                      in={isAnyFetching}
                      timeout={LOADING_DB_MS}
                      unmountOnExit={true}
                    >
                      <CircularProgress
                        size={16}
                        color="primary"
                        title={strLoading}
                        style={{ marginRight: '4px' }}
                      />
                    </Fade>
                    <IconButton
                      data-testid={`denum-search-clear-${fieldName}`}
                      title={strClear}
                      onClick={handleClearInput}
                      size="small"
                      disabled={false}
                      sx={clearIconSx}
                    >
                      <ClearIcon
                        sx={{
                          color: 'inherit',
                          fontSize: 'inherit',
                        }}
                      />
                    </IconButton>
                    <IconButton
                      data-testid={`denum-search-toggle-${fieldName}`}
                      ref={arrowBtnRef}
                      title={arrowBtnTxt}
                      onClick={handleArrowClick}
                      size="small"
                      sx={arrowIconSx}
                      disabled={false}
                    >
                      <ArrowDropDownIcon
                        sx={{
                          color: 'inherit',
                          fontSize: 'inherit',
                        }}
                      />
                    </IconButton>
                  </Box>
                ),
              },

              htmlInput: {
                'data-testid': `denum-search-${fieldName}`,
              },

              inputLabel: { shrink: true },
            }}
          />
        </Box>

        <Popper
          open={popperOpen}
          anchorEl={muiFieldRef?.current}
          data-testid={`denum-popper-${fieldName}`}
          sx={{ zIndex: theme.zIndex.modal }}
          popperOptions={{
            modifiers: [
              { name: 'offset', options: { offset: [0, 0] } },
              { name: 'flip', enabled: true },
              { name: 'preventOverflow', enabled: true, options: { padding: 1 } },
            ],
          }}
        >
          <ClickAwayListener
            mouseEvent="onPointerDown"
            touchEvent="onTouchEnd"
            onClickAway={handlePopperClose}
          >
            <Paper
              onKeyDown={handlePaperKeyDown}
              sx={{
                display: 'grid',
                gridTemplateRows: '1fr auto',
                gridTemplateAreas: `"options" "footer"`,
                width: muiFieldWidth || 'auto',
                minWidth: 150,
                minHeight: 45,
                maxHeight: DROPDOWN_MAX_HEIGHT,
                overflow: 'hidden',
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[3],
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <List
                dense={true}
                disablePadding={true}
                ref={listBoxRef}
                sx={{
                  gridArea: 'options',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'auto',
                }}
              >
                {fetchedOptions.map((option, idx) => {
                  // dropdown list reads fetchedOptions rather than
                  // dynOpts as this can support typeahead and we dont want
                  // static dynOpts forced showing up in a typeahead scenario
                  const optVal = option?.value ?? '';
                  const optLabel = option?.label ?? optVal;
                  const keyId = `denum-opt-${fieldName}-${optVal}`;
                  const isSelected = nValues.includes(option?.value);
                  const isFirstItem = idx === 0;
                  const isLastItem = idx === fetchedOptions.length - 1;
                  const handleRefs = handleListItemRefs({ isFirstItem, isLastItem });

                  // checkbox/radio button is just for visual styling
                  // selection action is handled by list item button
                  return (
                    <ListItemButton
                      key={keyId}
                      ref={handleRefs}
                      data-testid={keyId}
                      selected={isSelected}
                      disabled={disabled}
                      onClick={() => handleOptionToggle(optVal)}
                      dense={true}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row',
                        gap: 0.5,
                        px: 1,
                        py: 1,
                      }}
                    >
                      {isMultiSelect ? (
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          disableRipple={true}
                          tabIndex={-1}
                          sx={checkRadioSx}
                        />
                      ) : (
                        <Radio
                          checked={isSelected}
                          size="small"
                          disableRipple={true}
                          tabIndex={-1}
                          sx={checkRadioSx}
                        />
                      )}

                      <Typography sx={{ fontSize: bloxValues.smallFontSizeRem }}>
                        {optLabel}
                      </Typography>
                    </ListItemButton>
                  );
                })}
                <IntersectionLoader
                  hasNextPage={hasNextPage}
                  isFetching={isFetchingNextPage}
                  fetchNextPage={fetchNextPage}
                  hideOnPagesEnd={true}
                  ixRef={ixRef}
                  entry={entry}
                  compact={true}
                />
              </List>

              <Box
                sx={{
                  gridArea: 'footer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 0.5,
                  backgroundColor: theme.palette.action.hover,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: bloxValues.tinyFontSizeRem,
                    fontWeight: theme.typography.fontWeightLight,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {dbFooterText}
                </Typography>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </FormControl>
    );
  },
);
DenumFieldDropdown.displayName = 'DenumFieldDropdown';
